import {
  Box,
  Drawer,
  FormLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ActionButton,
  FieldInput,
  HorizontalProgressBar,
  IconClose,
  ToolbarSearchableSelectPopover,
  sanitizePhoneInput,
  type ToolbarSearchableSelectOption,
  validateCompanyNameText,
  validateEmailAddress,
  validateLettersSpacesCommasNameRequired,
} from "design-components";
import type { ChangeEvent, FormEvent } from "react";
import type { CustomerEditDrawerInitialValues } from "../../../shared/store/customers/customers.models";
import { useCustomerFormDrawer } from "../hooks/use-customer-form-drawer.hook";
import type { CustomerFormDrawerMode, CustomerFormSubmitPayload } from "../types";

const INVITE_DESCRIPTION =
  "An email will be sent to the email below with a link to set up their account.";
const EDIT_DESCRIPTION = "Update customer details and save your changes.";

export interface InviteCustomerDrawerProps {
  mode: CustomerFormDrawerMode;
  open: boolean;
  onClose: () => void;
  locationOptions: ToolbarSearchableSelectOption[];
  locationsLoading?: boolean;
  initialValues?: CustomerEditDrawerInitialValues | null;
  prefillLoading?: boolean;
  onSubmit: (payload: CustomerFormSubmitPayload) => void | Promise<void>;
  submitting?: boolean;
  customerId?: number;
}

export function InviteCustomerDrawer(props: InviteCustomerDrawerProps) {
  const {
    mode,
    open,
    onClose,
    locationOptions,
    locationsLoading = false,
    initialValues = null,
    prefillLoading = false,
    onSubmit,
    submitting = false,
    customerId,
  } = props;

  const {
    isEditMode,
    companyName,
    setCompanyName,
    primaryContactName,
    setPrimaryContactName,
    primaryContactEmail,
    setPrimaryContactEmail,
    phone,
    setPhone,
    primaryBusinessAddress,
    setPrimaryBusinessAddress,
    selectedLocationIds,
    setSelectedLocationIds,
    fieldErrors,
    setSingleFieldError,
    validateFieldOnBlur,
    validate,
    isFormValid,
    buildPayload,
    mergedLocationOptions,
    primaryBusinessAddressMaxLength,
  } = useCustomerFormDrawer({
    mode,
    open,
    locationOptions,
    initialValues,
    customerId,
  });

  const handleSubmitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || prefillLoading || !validate()) return;
    const payload = buildPayload();
    if (payload.locationIds.length === 0) {
      setSingleFieldError("locations", "Select at least one location");
      return;
    }
    await onSubmit(payload);
  };

  const handleCancel = () => {
    if (submitting || prefillLoading) return;
    onClose();
  };

  const drawerTitle = isEditMode ? "Edit customer" : "Invite customer";
  const submitLabel = isEditMode ? "Save changes" : "Invite customer";
  const description = isEditMode ? EDIT_DESCRIPTION : INVITE_DESCRIPTION;
  const formDisabled = submitting || prefillLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={formDisabled ? undefined : onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgb(255 255 255 / 50%)",
          },
        },
        paper: {
          className: "user-form-drawer__paper",
        },
      }}
    >
      <Box className="user-form-drawer" component="form" onSubmit={handleSubmitForm} noValidate>
        <Stack spacing={2.5} className="user-form-drawer__body">
          <Stack spacing={1.25} className="user-form-drawer__header-block">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography className="user-form-drawer__title" component="h2">
                {drawerTitle}
              </Typography>
              <IconButton
                type="button"
                onClick={handleCancel}
                disabled={formDisabled}
                aria-label="Close"
                className="user-form-drawer__close"
              >
                <IconClose aria-hidden size={24} />
              </IconButton>
            </Stack>
            <Typography className="user-form-drawer__description" variant="body2">
              {description}
            </Typography>
          </Stack>

          {prefillLoading ? (
            <Box
              className="customer-contact-form-drawer__loading"
              role="status"
              aria-label="Loading customer details"
              sx={{ px: 2 }}
            >
              <HorizontalProgressBar variant="indeterminate" width="100%" height={4} />
            </Box>
          ) : (
            <Box className="user-form-drawer__fields-scroll">
              <Stack spacing={0} className="user-form-drawer__fields">
                <FieldInput
                  label="Company name"
                  required
                  fullWidth
                  disabled={formDisabled}
                  error={Boolean(fieldErrors.companyName)}
                  helperText={fieldErrors.companyName}
                  outlinedInputProps={{
                    value: companyName,
                    inputProps: { maxLength: 50 },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      const v = e.target.value;
                      setCompanyName(v);
                      if (fieldErrors.companyName) {
                        setSingleFieldError("companyName", validateCompanyNameText(v));
                      }
                    },
                    onBlur: () => validateFieldOnBlur("companyName"),
                  }}
                />
                <FieldInput
                  label="Primary contact's name"
                  required
                  fullWidth
                  disabled={formDisabled}
                  error={Boolean(fieldErrors.primaryContactName)}
                  helperText={fieldErrors.primaryContactName}
                  outlinedInputProps={{
                    value: primaryContactName,
                    inputProps: { maxLength: 50 },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      const v = e.target.value;
                      setPrimaryContactName(v);
                      if (fieldErrors.primaryContactName) {
                        setSingleFieldError("primaryContactName", validateLettersSpacesCommasNameRequired(v));
                      }
                    },
                    onBlur: () => validateFieldOnBlur("primaryContactName"),
                  }}
                />
                <FieldInput
                  label="Primary contact's email"
                  required={!isEditMode}
                  fullWidth
                  disabled={formDisabled || isEditMode}
                  error={Boolean(fieldErrors.primaryContactEmail)}
                  helperText={fieldErrors.primaryContactEmail}
                  outlinedInputProps={{
                    type: "email",
                    value: primaryContactEmail,
                    readOnly: isEditMode,
                    inputProps: { maxLength: 50 },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      if (isEditMode) return;
                      const v = e.target.value;
                      setPrimaryContactEmail(v);
                      if (fieldErrors.primaryContactEmail) {
                        setSingleFieldError(
                          "primaryContactEmail",
                          validateEmailAddress(v, { required: true, minLen: 3, maxLen: 50 }),
                        );
                      }
                    },
                    onBlur: () => validateFieldOnBlur("primaryContactEmail"),
                  }}
                />
                <FieldInput
                  label="Phone"
                  fullWidth
                  disabled={formDisabled}
                  outlinedInputProps={{
                    value: phone,
                    inputProps: { inputMode: "tel", autoComplete: "tel" },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      setPhone(sanitizePhoneInput(e.target.value));
                    },
                  }}
                />
                <FieldInput
                  label="Primary business address"
                  fullWidth
                  disabled={formDisabled}
                  outlinedInputProps={{
                    value: primaryBusinessAddress,
                    inputProps: { maxLength: primaryBusinessAddressMaxLength },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      setPrimaryBusinessAddress(e.target.value);
                    },
                  }}
                />
                <Stack spacing={1.25} className="invite-customer-drawer__locations">
                  <Stack direction="row" spacing={0} className="input-label-row">
                    <FormLabel className="input-label" component="span">
                      Location(s)
                    </FormLabel>
                    <Typography component="span" className="input-required">
                      *
                    </Typography>
                  </Stack>
                  <ToolbarSearchableSelectPopover
                    className="toolbar-searchable-select-popover--form-field"
                    options={mergedLocationOptions}
                    value={selectedLocationIds}
                    disabled={formDisabled}
                    error={Boolean(fieldErrors.locations)}
                    onBlur={() => validateFieldOnBlur("locations")}
                    onChange={(ids) => {
                      setSelectedLocationIds(ids);
                      if (fieldErrors.locations) {
                        setSingleFieldError("locations", ids.length > 0 ? undefined : "Select at least one location");
                      }
                    }}
                    allValue=""
                    hideAllOption
                    emptySelectionDisplay="Select locations"
                    showTriggerChevron
                    triggerSelectedDisplay="chips"
                    ariaLabel="Select locations"
                    searchPlaceholder="Search"
                    loading={locationsLoading}
                    listMaxHeight={280}
                  />
                  {fieldErrors.locations ? (
                    <Typography variant="caption" className="input-helper-text Mui-error" role="alert">
                      {fieldErrors.locations}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={1.25} className="user-form-drawer__actions">
            <ActionButton
              type="submit"
              actionVariant="contained-primary"
              disabled={formDisabled || !isFormValid || prefillLoading}
              size="small"
            >
              {submitLabel}
            </ActionButton>
            <ActionButton
              type="button"
              actionVariant="neutral"
              onClick={handleCancel}
              disabled={formDisabled}
              size="small"
            >
              Cancel
            </ActionButton>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
